import { popModal, pushModal } from 'domain/store/root/root.actions';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Button } from './Button';
import { Group } from './Flex';
import { Modal } from './Modal';

import './ConfirmationModal.scss';
import { useRef } from 'react';
/**
 * ConfirmationModal
 *
 * This component is a modal that asks the user to confirm or cancel an action.
 */
export type ConfirmationModalProps = {
  title: string;
  message: string | React.ReactNode;
  className?: string;
  buttons?: React.ReactNode;
  onConfirm?: VoidFunction;
  onCancel?: VoidFunction;
};

export const useAwaitableConfirmationModal = (props: Pick<ConfirmationModalProps, 'title' | 'message'>) => {
  // Create a event listener that will return a promise
  const resolveRef = useRef<(value: boolean) => void>();

  const props2: ConfirmationModalProps = { ...props };
  props2.onConfirm = () => {
    if (resolveRef.current) {
      resolveRef.current(true);
    }
  }
  props2.onCancel = () => {
    if (resolveRef.current) {
      resolveRef.current(false);
    }
  };

  const [showConfirmationModal] = useConfirmationModal(props2);
  return () => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;      
      showConfirmationModal();
    });
  };
};

export const useConfirmationModal = (props: ConfirmationModalProps) => {
  const dispatch = useDispatch();

  const doClose = () => {
    dispatch(popModal());
  };

  const doConfirm = () => {
    if (props.onConfirm) {
      props.onConfirm();
    }
    doClose();
  };

  const doCancel = () => {
    if (props.onCancel) {
      props.onCancel();
    }
    doClose();
  };

  const open = () => {
    dispatch(
      pushModal(
        <Modal
          header={props.title}
          onClose={doCancel}
          className={`confirmation-modal ${props.className || ''}`}
          dismissable
        >
          <Group orientation='column'>
            <Group>
              <>{props.message ?? 'Confirm the action.'}</>
            </Group>
            <Group>
              {props.buttons ?? (
                <>
                  <Button onClick={doConfirm}>Confirm</Button>
                  <Button onClick={doCancel}>Cancel</Button>
                </>
              )}
            </Group>
          </Group>
        </Modal>
      )
    );
  };

  return [open, doCancel];
};
